import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  pointerWithin,
  type CollisionDetection,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { Spinner, useToast } from 'components/ui';
import { useSalesStore } from '../../stores/salesStore';
import { useUIStore } from 'stores';
import { getTranslation } from 'i18n';
import { KANBAN_COLUMNS } from '../../types/sales';
import type { SalesLeadStatus } from '../../types/sales';
import KanbanColumn from './KanbanColumn';
import LeadCard from './LeadCard';
import { computeKanbanPipelineOrder, sortLeadsByPipeline } from './kanbanOrder';

/** Prefer pointer-inside targets so column droppables register when moving between lists. */
const kanbanCollisionDetection: CollisionDetection = (args) => {
  const pointerHits = pointerWithin(args);
  if (pointerHits.length > 0) return pointerHits;
  return closestCorners(args);
};

const KanbanBoard: React.FC = () => {
  const {
    leads, leadsLoading, fetchLeads,
    updateLead, optimisticKanbanUpdate,
  } = useSalesStore();
  const language = useUIStore((s) => s.language);
  const t = (key: string) => getTranslation(language, key);
  const toast = useToast();
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    fetchLeads();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const leadsByStatus = useMemo(() => {
    const map: Record<string, typeof leads> = {};
    for (const col of KANBAN_COLUMNS) map[col] = [];
    for (const lead of leads) {
      if (map[lead.status]) map[lead.status].push(lead);
    }
    for (const col of KANBAN_COLUMNS) {
      map[col].sort(sortLeadsByPipeline);
    }
    return map;
  }, [leads]);

  const activeLead = useMemo(
    () => (activeId ? leads.find((l) => l.id === activeId) : null),
    [activeId, leads],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const leadId = active.id as string;
      const overId = over.id as string;
      const lead = leads.find((l) => l.id === leadId);
      if (!lead) return;

      let targetStatus: SalesLeadStatus;
      if (KANBAN_COLUMNS.includes(overId as SalesLeadStatus)) {
        targetStatus = overId as SalesLeadStatus;
      } else {
        const overLead = leads.find((l) => l.id === overId);
        if (!overLead) return;
        targetStatus = overLead.status;
      }

      const computed = computeKanbanPipelineOrder(leads, leadId, targetStatus, overId, KANBAN_COLUMNS);
      if (!computed || !computed.changed) return;

      const { newPipelineOrder, newStatus } = computed;
      const prevSnapshot = { status: lead.status, pipeline_order: lead.pipeline_order ?? 0 };

      optimisticKanbanUpdate(leadId, { status: newStatus, pipeline_order: newPipelineOrder });

      const payload: { status?: string; pipeline_order: number } = { pipeline_order: newPipelineOrder };
      if (lead.status !== newStatus) payload.status = newStatus;

      const result = await updateLead(leadId, payload);
      if (!result) {
        optimisticKanbanUpdate(leadId, prevSnapshot);
        toast.error(t('salesKanbanErrorUpdateStatus'));
      }
    },
    [leads, optimisticKanbanUpdate, updateLead, toast, t],
  );

  if (leadsLoading && leads.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-1 pb-4">
        <div className="text-sm text-content-secondary">
          {leads.length !== 1
            ? t('salesKanbanLeadsInPipelinePlural').replace('{{count}}', String(leads.length))
            : t('salesKanbanLeadsInPipeline').replace('{{count}}', String(leads.length))}
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={kanbanCollisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-3 overflow-x-auto pb-4 flex-1 min-h-0 w-full min-w-0">
          {KANBAN_COLUMNS.map((status) => (
            <KanbanColumn
              key={status}
              status={status}
              leads={leadsByStatus[status] || []}
            />
          ))}
        </div>

        <DragOverlay>
          {activeLead ? <LeadCard lead={activeLead} isDragOverlay /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default KanbanBoard;
